import {
  SuccessNotification,
  ErrorNotification,
} from '@/store/modules/notifications/notifications';
import axios from 'axios';
import { actions, mutations, state as stateTypes } from './types';

export default {
  /**
   * Fetch all datasets/studies from server.
   * @param {Object} Context
   */
  async [actions.FETCH_DATASETS]({ commit, dispatch }) {
    commit(mutations.SET_LOADING, true);
    commit(mutations.SET_DATASETS, []);

    try {
      const { data } = await axios.get(
        '/api/projects?include=studies&include=num_subjects'
      );
      const datasets = data.projects
        .map(project =>
          project.studies.map(study => ({
            ...project,
            ...study,
          }))
        )
        .flat();
      commit(mutations.SET_DATASETS, datasets);
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
      commit(mutations.SET_LOADING, false);
    }
    commit(mutations.SET_LOADING, false);
  },
  [actions.SELECT_DATASETS]({ commit }, selectedDatasets) {
    commit(mutations.SET_SELECTED_DATASETS, selectedDatasets);
  },
  /**
   * Save collection to the database.
   * @param {Object} context
   * @param {Object} payload
   */
  async [actions.SAVE_COLLECTION]({ commit, dispatch }, payload) {
    commit(mutations.SET_LOADING, true);

    try {
      const { data } = await axios.post('/api/collections', {
        label: payload.collectionName,
        study_ids: payload.datasetIds,
        variables: payload.variables,
      });

      commit(mutations.ADD_COLLECTION, data.collection);
      commit(mutations.SET_LOADING, false);

      const notification = new SuccessNotification(
        'Study Dataset successfully saved.'
      );

      commit(mutations.CLEAR_SELECTED_DATASETS);
      dispatch(notification.dispatch, notification, { root: true });
      return new Promise(resolve => {
        resolve(data.collection);
      });
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
      commit(mutations.SET_LOADING, false);
      return new Promise((resolve, reject) => reject());
    }
  },
  /**
   * Fetch all of user's collections.
   * @param {Object} context
   */
  async [actions.FETCH_COLLECTIONS](
    { commit, dispatch },
    requirePureToneAudiometry
  ) {
    commit(mutations.SET_LOADING, true);

    try {
      let collections_uri =
        '/api/collections?include=cohort_counts&include=analysis_counts&include=studies&include=variable_visits_set';
      if (requirePureToneAudiometry) {
        collections_uri = collections_uri + '&require=pure_tone_audiometry';
      }
      const { data } = await axios.get(collections_uri);
      data.collections.forEach(c => {
        c.date_generated_epoch = new Date(c.date_generated).getTime();
        c.has_visits_set = false;
        if ('observation_variables' in c) {
          c.has_visits_set = true;
          c.observation_variables.forEach(v => {
            if (
              (v.first_visit_event == null && v.first_visit_num == null) ||
              (v.last_visit_event == null && v.last_visit_num == null)
            ) {
              c.has_visits_set = false;
            }
          });
          c.observation_variables_list = c.observation_variables;
        }
      });
      data.collections.sort((a, b) => {
        return b.date_generated_epoch - a.date_generated_epoch;
      });
      commit(mutations.SET_COLLECTIONS, data.collections);
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
    }

    commit(mutations.SET_LOADING, false);
  },
  /**
   * Fetch a single collection.
   * @param {Object} context
   */
  async [actions.FETCH_COLLECTION]({ commit, dispatch }, collectionId) {
    commit(mutations.SET_LOADING, true);

    try {
      const { data } = await axios.get(
        '/api/collections/' +
          collectionId +
          '?include=cohorts&include=variables&include=studies'
      );
      var c = data.collection;
      c.date_generated_epoch = new Date(c.date_generated).getTime();
      c.has_visits_set = true;
      c.subject_variables.forEach(v => {
        if (v.ontology.label == 'Dataset') {
          v.ontology.category = 'Dataset';
        }
      });
      c.observation_variables.forEach(v => {
        if (
          (v.first_visit_event == null && v.first_visit_num == null) ||
          (v.last_visit_event == null && v.last_visit_num == null)
        ) {
          c.has_visits_set = false;
        }
      });
      commit(mutations.SET_COLLECTION, c);
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
    }
    commit(mutations.SET_LOADING, false);
  },
  async [actions.RENAME_COLLECTION](
    { commit, dispatch },
    { collectionId, collectionName }
  ) {
    try {
      await axios.post(`/api/collections/${collectionId}/rename`, {
        collection_name: collectionName,
      });

      let args = {
        collectionId: collectionId,
        collectionName: collectionName,
      };
      commit(mutations.RENAME_COLLECTION, args);

      const notification = new SuccessNotification(`Successfully renamed`);
      dispatch(notification.dispatch, notification, { root: true });
    } catch ({ response }) {
      const notification = new ErrorNotification(response.data.error);
      dispatch(notification.dispatch, notification, { root: true });
    }
  },
  async [actions.DELETE_COLLECTION]({ commit, dispatch }, collectionId) {
    commit(mutations.SET_LOADING, true);

    try {
      await axios.delete(`/api/collections/${collectionId}`);
      commit(mutations.DELETE_COLLECTION, collectionId);
      const notification = new SuccessNotification(
        'Study Dataset successfully deleted.'
      );
      dispatch(notification.dispatch, notification, { root: true });
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
    } finally {
      commit(mutations.SET_LOADING, false);
    }
  },
  [actions.CLEAR_COLLECTION]({ commit }) {
    commit(mutations.SET_COLLECTION, null);
  },
  async [actions.FETCH_OBS_VAR_COUNTS]({ commit, dispatch, state }, payload) {
    const { datasetId, varId } = payload;
    let ovc = state[stateTypes.OBS_VAR_COUNTS];
    if (!(datasetId in ovc) || !(varId in ovc[datasetId])) {
      try {
        const { data } = await axios.get(
          `/api/studies/${datasetId}/variables/${varId}/distribution`
        );
        var counts = data.counts;
        commit(mutations.SET_OBS_VAR_COUNTS, { datasetId, varId, counts });
      } catch (err) {
        const notification = new ErrorNotification(err);
        dispatch(notification.dispatch, notification, { root: true });
      }
    }
    return ovc[datasetId][varId];
  },
  async [actions.FETCH_SUBJ_VAR_COUNTS]({ commit, dispatch, state }, payload) {
    const { datasetId, varId } = payload;
    let svc = state[stateTypes.SUBJ_VAR_COUNTS];
    if (!(datasetId in svc) || !(varId in svc[datasetId])) {
      try {
        const { data } = await axios.get(
          `/api/studies/${datasetId}/subjects/variables/${varId}/distribution`
        );
        var counts = data.counts;
        commit(mutations.SET_SUBJ_VAR_COUNTS, { datasetId, varId, counts });
      } catch (err) {
        const notification = new ErrorNotification(err);
        dispatch(notification.dispatch, notification, { root: true });
      }
    }
    return svc[datasetId][varId];
  },
  async [actions.GET_VARIABLE_DATA]({ dispatch }, payload) {
    const { datasetId, varId } = payload;

    try {
      const { data } = await axios.post(
        `/api/studies/${datasetId}/variable_data`,
        { variable_ids: [varId] }
      );
      return data.data;
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
    }
  },
};
